import { createContext, useContext, useCallback, useMemo } from "react";
import { shallowEqual } from "react-redux";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import {
  setLeadFilterField,
  setNicheFilterField,
  applyFilters,
} from "@/features/leads/stores/leadFilterSlice";
import {
  setDynamicFilterField,
  setDynamicNicheFilterField,
  selectDynamicFilters,
  applyDynamicFilters,
} from "@/features/dynamic/store/dynamicFilterSlice";

// ── Shared Filter State Shape ──────────────────────────────────────
// ── Shared Filter State Shape ──────────────────────────────────────
// Moved to ./filterState (a leaf) to break the FilterContext ↔
// dynamicFilterSlice import cycle. Re-exported so every existing importer of
// `FilterState` / `defaultFilterState` from this file keeps working.
export type { FilterState } from "./filterState";
export { defaultFilterState } from "./filterState";
import type { FilterState } from "./filterState";

// ── Context Type ───────────────────────────────────────────────────
type FilterUpdater = <K extends keyof FilterState>(
  key: K,
  value: FilterState[K]
) => void;

type NicheFilterUpdater = (
  key: string,
  value: string | number | boolean
) => void;

interface FilterContextType {
  filters: FilterState;
  updateFilter: FilterUpdater;
  updateNicheFilter: NicheFilterUpdater;
  /**
   * Commits the draft to `appliedFilters` — the same thing the drawer's
   * "Apply filters" button does, routed to whichever slice owns this view.
   *
   * For panels that ASK the user something in their own dialog: once they hit
   * Apply there, making them hunt for a second Apply reads as the first one
   * having failed. Redux dispatches synchronously, so calling this straight
   * after `updateFilter` sees the new values.
   */
  commitFilters: () => void;
  /**
   * Stage id pinned by the ROUTE on a dynamic stage tab (e.g. `/Qualify`) —
   * `filters.stage` stays empty there because the user can't change it from
   * the panel. `null` on the /leads page, where the stage comes from the
   * header picker into `filters.stage`. Panels that describe what the filters
   * are scoped to (the "Number of deals" hint) need the effective stage,
   * not just the one the user typed.
   */
  pinnedStageId: string | number | null;
}

const FilterContext = createContext<FilterContextType | null>(null);

// ── Hooks (used by all sub-components) ─────────────────────────────
export function useFilterValues(): FilterState {
  const ctx = useContext(FilterContext);
  if (!ctx)
    throw new Error("useFilterValues must be used within a FilterProvider");
  return ctx.filters;
}

/** Commits the draft filters (see `commitFilters`). */
export function useFilterCommit(): () => void {
  const ctx = useContext(FilterContext);
  if (!ctx)
    throw new Error("useFilterCommit must be used within a FilterProvider");
  return ctx.commitFilters;
}

export function useFilterUpdate(): FilterUpdater {
  const ctx = useContext(FilterContext);
  if (!ctx)
    throw new Error("useFilterUpdate must be used within a FilterProvider");
  return ctx.updateFilter;
}

/**
 * The stage the current view is pinned to by its route, or `null` on /leads.
 * Combine with `filters.stage` for the EFFECTIVE stage selection.
 */
export function useFilterPinnedStageId(): string | number | null {
  return useContext(FilterContext)?.pinnedStageId ?? null;
}

export function useNicheFilterUpdate(): NicheFilterUpdater {
  const ctx = useContext(FilterContext);
  if (!ctx)
    throw new Error(
      "useNicheFilterUpdate must be used within a FilterProvider"
    );
  return ctx.updateNicheFilter;
}

// ── Provider for the Leads page (global state) ────────────────────
export function LeadFilterProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const dispatch = useAppDispatch();
  const filters = useAppSelector(
    (state) => state.leadFilterSlice,
    shallowEqual
  ) as FilterState;

  const updateFilter = useCallback<FilterUpdater>(
    (key, value) => {
      dispatch(setLeadFilterField({ key, value }));
    },
    [dispatch]
  );

  const updateNicheFilter = useCallback<NicheFilterUpdater>(
    (key, value) => {
      dispatch(setNicheFilterField({ key, value }));
    },
    [dispatch]
  );

  const commitFilters = useCallback(() => {
    dispatch(applyFilters());
  }, [dispatch]);

  const ctx = useMemo(
    () => ({
      filters,
      updateFilter,
      updateNicheFilter,
      commitFilters,
      pinnedStageId: null,
    }),
    [filters, updateFilter, updateNicheFilter, commitFilters]
  );

  return (
    <FilterContext.Provider value={ctx}>{children}</FilterContext.Provider>
  );
}

// ── Provider for the Dynamic page (per-route state) ───────────────
export function DynamicFilterProvider({
  routeId,
  children,
}: {
  routeId: string | number;
  children: React.ReactNode;
}) {
  const dispatch = useAppDispatch();
  const filters = useAppSelector(
    (state) => selectDynamicFilters(state, routeId),
    shallowEqual
  ) as FilterState;

  const updateFilter = useCallback<FilterUpdater>(
    (key, value) => {
      dispatch(setDynamicFilterField({ routeId, key, value }));
    },
    [dispatch, routeId]
  );

  const updateNicheFilter = useCallback<NicheFilterUpdater>(
    (key, value) => {
      dispatch(setDynamicNicheFilterField({ routeId, key, value }));
    },
    [dispatch, routeId]
  );

  const commitFilters = useCallback(() => {
    dispatch(applyDynamicFilters(routeId));
  }, [dispatch, routeId]);

  const ctx = useMemo(
    // The dynamic route IS the stage (`routeId` is the stage id — same value
    // `DynamicsStageStatusPicker` passes as `forcedStageId`).
    () => ({
      filters,
      updateFilter,
      updateNicheFilter,
      commitFilters,
      // A Favorite View route (`view-<id>`) pins no stage — it isn't one.
      // Passing the route id through here would have panels resolve a stage
      // that doesn't exist.
      pinnedStageId:
        typeof routeId === "string" && routeId.startsWith("view-")
          ? null
          : routeId,
    }),
    [filters, updateFilter, updateNicheFilter, commitFilters, routeId]
  );

  return (
    <FilterContext.Provider value={ctx}>{children}</FilterContext.Provider>
  );
}
